import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button, TextField, FieldHint, Information } from 'ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from '@/i18n/client';
import { emailPattern, isEmptyObject, setCookie } from 'utils';
import { signIn } from '@/modules/account/api';
import { addSeconds } from 'date-fns';
import { COOKIE_ACCESS_TOKEN, COOKIE_REFRESH_TOKEN } from '@/modules/product/utils';
import { useNotifications } from '@/shared/context/notifications-context';

import { SocialSign } from '../SocialSign';

import { SignInFormModel, SignInFormProps } from './types';
import styles from './SignInForm.module.scss';

export const SignInForm = ({
	accountConfirmation,
	hasAccount = false,
	setRemindPasswordActive,
	handleModalClose,
	remindPasswordClickCallback,
	setChangePasswordActive,
}: SignInFormProps) => {
	const { t } = useTranslation(['header', 'common', 'checkout']);
	const router = useRouter();
	const { showNotification } = useNotifications();
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<SignInFormModel>();

	const onSubmit = async (data: SignInFormModel) => {
		setIsLoading(true);

		const formData = {
			email: data.email,
			plainPassword: data.password,
		};

		const response = await signIn(formData);

		if (response === false) {
			setChangePasswordActive(data.email);
			return;
		}

		if (!response || !response?.accessToken) {
			showNotification({
				id: 'login-failed',
				type: 'error',
				message: t('authModal.signInWrongEmailOrPassword'),
			});
			setIsLoading(false);
			return;
		}

		const { accessToken, accessTokenExpiresIn, refreshToken, refreshTokenExpiresIn } = response;

		setCookie(COOKIE_ACCESS_TOKEN, accessToken, {
			expires: addSeconds(new Date(), accessTokenExpiresIn),
		});

		setCookie(COOKIE_REFRESH_TOKEN, refreshToken, {
			expires: addSeconds(new Date(), refreshTokenExpiresIn),
		});

		showNotification({
			id: 'login-success',
			type: 'success',
			message: t('authModal.signInSuccess'),
		});
		router.refresh();
		handleModalClose();
	};

	const handleRemindPasswordPress = () => {
		setRemindPasswordActive((oldState) => !oldState);

		if (!remindPasswordClickCallback) {
			return;
		}

		setTimeout(() => {
			remindPasswordClickCallback();
		}, 300);
	};

	return (
		<>
			<div className={styles.signInForm__formWrapper}>
				<h2 className={styles.signInForm__title}>
					{accountConfirmation
						? `${t('common:common.success')}!`
						: hasAccount
						? t('authModal.helloAgainTitle')
						: t('authModal.signInTitle')}
				</h2>
				{accountConfirmation ? (
					<Information className={styles.signInForm__information} icon="check" type="success">
						{t('authModal.accountConfirmationCaption')}
					</Information>
				) : null}
				{hasAccount ? (
					<p className={styles.signInForm__caption}>{t('authModal.helloAgainCaption')}</p>
				) : null}
				<form onSubmit={handleSubmit(onSubmit)}>
					<TextField
						className={styles.signInForm__emailField}
						label={t('common:common.emailAddress')}
						{...register('email', {
							required: t('checkout:details.fieldRequired') || '',
							pattern: {
								value: emailPattern,
								message: t('checkout:details.invalidEmail') || '',
							},
						})}
						hasError={!!errors.email}
					/>
					{errors.email ? <FieldHint isError>{errors.email?.message}</FieldHint> : null}
					<TextField
						type={showPassword ? 'text' : 'password'}
						className={styles.signInForm__passwordField}
						label={t('common:common.password')}
						suffix={
							<Button
								buttonName="showButton"
								type="button"
								variant="iconButton"
								onPress={() => setShowPassword((oldState) => !oldState)}
							>
								{showPassword ? t('common:common.hide') : t('common:common.show')}
							</Button>
						}
						{...register('password', {
							required: t('checkout:details.fieldRequired') || '',
						})}
						hasError={!!errors.password}
					/>
					{errors.password ? <FieldHint isError>{errors.password?.message}</FieldHint> : null}
					<Button
						buttonName="signInButton"
						fullWidth
						variant="blue"
						type="submit"
						isLoading={isLoading}
						isDisabled={isLoading || !isEmptyObject(errors)}
					>
						{t('authModal.signIn')}
					</Button>
				</form>
				<Button
					variant="iconButton"
					buttonName="remindPasswordButton"
					onPress={handleRemindPasswordPress}
				>
					{t('authModal.remindPassword')}
				</Button>
			</div>
			{/* <SocialSign /> */}
		</>
	);
};
